import { useState } from 'react';
import fileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUiContext } from '~/common/components/context/useUiContext';
import { toWebshopApiError } from '~/common/types/webshopApiError';

/**
 * Hook to download a file from a given URL.
 */
export function useDownload() {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const { showUnauthorizedDialog } = useUiContext();

  const download = async (downloadUrl: string, filename: string) => {
    setIsDownloading(true);

    try {
      const response = await fetch(downloadUrl);

      if (response.status === 401) {
        showUnauthorizedDialog();
        return;
      }

      if (response.ok) {
        const blob = await response.blob();
        fileSaver.saveAs(blob, filename);
        return;
      }

      const apiError = await toWebshopApiError(
        response,
        t('Download fehlgeschlagen!'),
        downloadUrl
      );
      const suffix = apiError.traceId ? ` (ID: ${apiError.traceId})` : '';
      toast.error(`${apiError.message}${suffix}`);
    } catch {
      toast.error(t('Download fehlgeschlagen!'));
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, download };
}
